import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserRolesEnum } from '@shared/constants/auth.constants';
import {
  CampaignDto,
  useCampaignUpdateControllerCampaignUpdateMutation,
} from '@shared/services/apiService/apiService';
import type { UserRoles } from '@shared/services/apiService/apiService';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';
import {
  resolveAmbienceSettingsErrorMessage,
  showAmbienceSettingsErrorToast,
  showAmbienceSettingsSuccessToast,
} from '@features/Admin/AmbienceSettings';

import { mapCampaignNoiseProcessingDraftToRequest } from '../utils/campaignNoiseProcessing.utils';
import { useCampaignNoiseProcessingController } from './useCampaignNoiseProcessingController';

interface UseCampaignNoiseProcessingSettingsOptions {
  campaign?: CampaignDto;
  currentUserRole?: UserRoles;
  refetch: () => Promise<unknown>;
}

export const useCampaignNoiseProcessingSettings = ({
  campaign,
  currentUserRole,
  refetch,
}: UseCampaignNoiseProcessingSettingsOptions) => {
  const { t } = useTranslation();
  const [updateCampaign, { isLoading: isSaving }] =
    useCampaignUpdateControllerCampaignUpdateMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const isReadOnly =
    currentUserRole === UserRolesEnum.VIEWER || campaign?.status !== CampaignRunStatusType.INACTIVE;
  const controller = useCampaignNoiseProcessingController({
    noiseProcessing: campaign?.noiseProcessing,
    isReadOnly,
    enabled: Boolean(campaign),
  });
  const hasValidationErrors = useMemo(
    () =>
      Object.values(controller.validationErrors).some((errors) => Object.keys(errors).length > 0),
    [controller.validationErrors],
  );
  const visibleValidationErrors = hasAttemptedSave
    ? controller.validationErrors
    : { noiseGuard: {}, noiseCancellation: {} };

  useEffect(() => {
    if (!hasValidationErrors) {
      setHasAttemptedSave(false);
    }
  }, [hasValidationErrors]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!campaign?.id || !controller.draft || isReadOnly) {
      return false;
    }

    setSaveError(null);

    const draftToSave = controller.draft;

    if (hasValidationErrors) {
      setHasAttemptedSave(true);
      return false;
    }

    try {
      await updateCampaign({
        id: campaign.id,
        campaignUpdateRequestDto: {
          noiseProcessing: mapCampaignNoiseProcessingDraftToRequest(draftToSave),
        },
      }).unwrap();
      controller.markSaved(draftToSave);
      showAmbienceSettingsSuccessToast({
        title: t('common.saved'),
        description: t('campaignPage.noiseProcessing.saveSuccessDescription'),
      });
      setHasAttemptedSave(false);
      await refetch();
      return true;
    } catch (error) {
      const message = resolveAmbienceSettingsErrorMessage(
        error,
        t('campaignPage.noiseProcessing.saveErrorDescription'),
      );
      setSaveError(message);
      showAmbienceSettingsErrorToast(message);
      return false;
    }
  }, [
    campaign?.id,
    controller.draft,
    controller.markSaved,
    hasValidationErrors,
    isReadOnly,
    refetch,
    t,
    updateCampaign,
  ]);

  const cancel = useCallback(() => {
    setSaveError(null);
    setHasAttemptedSave(false);
    controller.resetDraft();
  }, [controller]);

  return {
    ...controller,
    isReadOnly,
    hasValidationErrors,
    visibleValidationErrors,
    saveError,
    isSaving,
    save,
    cancel,
  };
};
